import { useCallback, useEffect } from 'react'

import { useLogin } from '@privy-io/react-auth'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { usePrevious } from 'react-use'

import {
  clearPendingModalContent,
  getPendingModalContent,
} from '~/components/smart-accounts/useRecoverySignerModal'
import { useAuth } from '~/context/AuthProvider'

/**
 * Restores the transaction approval dialog after a recovery signer's auth UI
 * closes.
 *
 * When a recovery signer is selected, the global modal closes so Privy's login
 * UI (email signers) or RainbowKit (wallet signers) can run without Radix
 * Dialog's overlay intercepting clicks. This hook detects when that UI closes,
 * whether it completed or was cancelled, and re-opens the modal with the saved
 * transaction dialog content.
 *
 * Must be rendered in a component that is always mounted and inside both
 * PrivyProvider and AuthContext (e.g. AppContainer).
 */
export const useRecoveryAuthRestore = () => {
  const { setGlobalModalState } = useAuth()

  const restore = useCallback(() => {
    const content = getPendingModalContent()
    if (content) {
      clearPendingModalContent()
      setGlobalModalState({ isOpen: true, content })
    }
  }, [setGlobalModalState])

  useLogin({
    onComplete: restore,
    onError: restore,
  })

  // RainbowKit closes its own modal on connect and on cancel; it exposes no
  // callback for either, so watch the open flag's falling edge instead.
  const { connectModalOpen } = useConnectModal()
  const wasConnectModalOpen = usePrevious(connectModalOpen)
  // eslint-disable-next-line local/noUseEffect
  useEffect(() => {
    if (wasConnectModalOpen && !connectModalOpen) restore()
  }, [wasConnectModalOpen, connectModalOpen, restore])
}
